import React from "react";

import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/components/ui/utils";
import { AccessionBoard } from "@/features/board/components/AccessionBoard";
import { AccessionBoardProvider } from "@/features/board/components/AccessionBoardProvider";
import { useView } from "@/features/view/components/ViewProvider";
import { useNavSelected } from "@/utils/useNavStack";
import { useAccessionListQuery } from "../hooks/useAccessionList";
import { AccessionGrid } from "./AccessionGrid";
import { AccessionListEmptyState } from "./AccessionListEmptyState";
import { AccessionTable } from "./AccessionTable";

type AccessionDisplayProps = {
  scrollRestorationId: string;
};

export function AccessionDisplay(props: AccessionDisplayProps) {
  const { scrollRestorationId } = props;
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const { settings } = useView();
  const { selected } = useNavSelected();

  return (
    <div
      className={cn(
        settings.display === "BOARD"
          ? "flex gap-3 flex-1 overflow-y-hidden overflow-x-scroll bg-background snap-x snap-mandatory sm:snap-none"
          : "h-full w-full overflow-auto relative",
      )}
      style={{ overflowAnchor: "none" }}
      data-scroll-restoration-id={scrollRestorationId}
      ref={scrollRef}
    >
      <AccessionDisplayInner
        scrollRef={scrollRef}
        selected={selected}
        {...props}
      />
    </div>
  );
}

function AccessionDisplayInner({
  scrollRef,
  scrollRestorationId,
  selected,
}: AccessionDisplayProps & {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  selected?: string | number | null;
}) {
  const { isMobile } = useSidebar();
  const { data, isPending } = useAccessionListQuery();
  const accessions = data || [];
  const { settings } = useView();

  if (!isPending && accessions.length === 0) {
    return (
      <div className="flex flex-1 min-h-[min(420px,65vh)] min-w-0 w-full items-center justify-center px-4 py-8">
        <AccessionListEmptyState />
      </div>
    );
  }

  // Accession Grid / List format - force to list when mobile and display is table
  if (
    settings.display === "LIST" ||
    (isMobile && settings.display === "TABLE")
  ) {
    return (
      <AccessionGrid
        accessions={accessions}
        isLoading={isPending}
        scrollRef={scrollRef}
        scrollRestorationId={scrollRestorationId}
        selected={selected}
      />
    );
  }

  if (settings.display === "BOARD") {
    return (
      <AccessionBoardProvider>
        <AccessionBoard accessions={accessions} loading={isPending} />
      </AccessionBoardProvider>
    );
  }

  return (
    <AccessionTable
      accessions={accessions}
      isLoading={isPending}
      scrollRef={scrollRef}
      scrollRestorationId={scrollRestorationId}
      selected={selected}
    />
  );
}
